import { useState } from 'react';
import { REFRESHING_HEIGHT } from '@hooks/usePullToRefresh';
import styles from './pull-refresh-indicator.module.css';

const DOT_COLORS = ['#FFB300', '#3EC6C6', '#F44A66', '#88967E'] as const;
const DOT_CLASSES = [styles.dot1, styles.dot2, styles.dot3, styles.dot4] as const;
const COLLAPSE_DURATION_MS = 200;

interface PullRefreshIndicatorProps {
  pullDistance: number;
  isRefreshing: boolean;
  maxPullDistance: number;
}

export function PullRefreshIndicator({
  pullDistance,
  isRefreshing,
  maxPullDistance,
}: PullRefreshIndicatorProps) {
  const isActive = pullDistance > 0 || isRefreshing;
  const [mounted, setMounted] = useState(false);

  // Mount synchronously when active (no frame delay)
  if (isActive && !mounted) {
    setMounted(true);
  }

  if (!mounted) return null;

  const height = isRefreshing ? REFRESHING_HEIGHT : pullDistance;
  const scale = isRefreshing ? 1 : Math.min(pullDistance / maxPullDistance, 1);

  return (
    <div
      className="overflow-hidden flex items-center justify-center"
      aria-hidden="true"
      style={{
        height,
        transition: isActive ? 'none' : `height ${COLLAPSE_DURATION_MS}ms ease-out`,
      }}
      onTransitionEnd={() => {
        if (!isActive) setMounted(false);
      }}
    >
      <div className="flex items-center justify-center gap-1">
        {DOT_COLORS.map((color, i) => (
          <div
            key={color}
            className={`${styles.dot} ${isRefreshing ? `${styles.dotBounce} ${DOT_CLASSES[i]}` : ''}`}
            style={{
              backgroundColor: color,
              transform: isRefreshing ? undefined : `scale(${scale})`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
